/**
 * Review and Approval, plus Campaign Created (PRD 10.2).
 *
 * The approval gate is the whole point of this screen: an approver sees the
 * final brief, the selected assets, a live re-check of availability and price,
 * and must tick every assumption before the approve button unlocks.
 */

import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { RequestFailed, api, saveBlob } from '../lib/api'
import { useIdentity } from '../hooks/useSession'
import type { AssetClass, PlanResponse, PlanVersionOut, RevalidationResult, SelectedAsset } from '../lib/types'
import { ASSET_CLASS_LABEL, dateRange, money, moneyShort, percent, shortDate } from '../lib/format'
import { Callout, EmptyState, ErrorCallout } from '../components/Callout'
import { Chip } from '../components/Chips'
import { InfoTip } from '../components/InfoTip'
import { FeatureMetric, Metric, Skeleton } from '../components/Metric'
import { AssetMap, type MapAsset } from '../components/AssetMap'
import { StepIndicator } from '../components/StepIndicator'

const PACKAGE_OPTIONS = ['-', 'Platinum', 'Gold', 'Silver']

export function PlanReview() {
  const { planId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { me } = useIdentity()
  const [version, setVersion] = useState<number | null>(null)
  const [acknowledged, setAcknowledged] = useState<string[]>([])
  const [comments, setComments] = useState('')
  const [revalidation, setRevalidation] = useState<RevalidationResult | null>(null)
  const [selectedMediaTypes, setSelectedMediaTypes] = useState<string[]>([])
  const [selectedAssetClasses, setSelectedAssetClasses] = useState<string[]>([])
  const [blockingDownload, setBlockingDownload] = useState<{ filename: string } | null>(null)
  const [blockingForm, setBlockingForm] = useState({
    campaignName: '',
    packageType: '-',
    wrapType: '',
    focDays: 0,
    digitalSlot: '',
    agencyCommissionPct: 0,
  })
  const [bookingConfirmForm, setBookingConfirmForm] = useState({ capturaCampaignId: '', notes: '' })

  const plans = useQuery<PlanResponse[]>({
    queryKey: ['plans'],
    queryFn: api.listPlans,
    enabled: !planId,
  })

  const plan = useQuery<PlanResponse>({
    queryKey: ['plan', planId],
    queryFn: () => api.getPlan(planId!),
    enabled: Boolean(planId),
  })

  const activeVersion = version ?? plan.data?.current_version ?? null

  const detail = useQuery<PlanVersionOut>({
    queryKey: ['plan-version', planId, activeVersion],
    queryFn: () => api.getVersion(planId!, activeVersion!),
    enabled: Boolean(planId && activeVersion),
  })

  const approvals = useQuery({
    queryKey: ['approvals', planId],
    queryFn: () => api.approvals(planId!),
    enabled: Boolean(planId),
  })

  // All hooks must be called before any conditional returns - moved here
  const availableMediaTypes = useMemo(() => {
    const assets = detail.data?.selected_assets ?? []
    const types = new Set(assets.map(a => a.media_type).filter(Boolean))
    return Array.from(types).sort()
  }, [detail.data])

  const availableAssetClasses = useMemo(() => {
    const assets = detail.data?.selected_assets ?? []
    const classes = new Set(
      assets
        .map(a => a.asset_class)
        .filter(Boolean)
        .map(c => {
          const label = ASSET_CLASS_LABEL[c as AssetClass]
          return label || (c === 'static' ? 'Static' : c === 'transit' ? 'Transit' : c === 'dooh' ? 'DOOH' : c)
        })
    )
    return Array.from(classes).sort()
  }, [detail.data])

  const filteredAssets = useMemo(() => {
    let assets = detail.data?.selected_assets ?? []

    if (selectedMediaTypes.length > 0) {
      assets = assets.filter(a => a.media_type && selectedMediaTypes.includes(a.media_type))
    }

    if (selectedAssetClasses.length > 0) {
      assets = assets.filter(a => {
        if (!a.asset_class) return false
        const label = ASSET_CLASS_LABEL[a.asset_class as AssetClass]
        const displayClass = label || (a.asset_class === 'static' ? 'Static' : a.asset_class === 'transit' ? 'Transit' : a.asset_class === 'dooh' ? 'DOOH' : a.asset_class)
        return selectedAssetClasses.includes(displayClass)
      })
    }

    return assets
  }, [detail.data, selectedMediaTypes, selectedAssetClasses])

  const mapAssets: MapAsset[] = useMemo(
    () =>
      filteredAssets
        .filter((a) => a.latitude !== null && a.longitude !== null)
        .map((a, i) => ({
          id: a.captura_asset_id,
          name: a.name,
          latitude: a.latitude!,
          longitude: a.longitude!,
          rank: i + 1,
          selected: true,
          score: a.total_score,
          mediaType: a.media_type,
          locality: a.locality,
          available: true,
        })),
    [filteredAssets],
  )

  const campaignWindow = useMemo(() => {
    const assets = detail.data?.selected_assets ?? []
    if (assets.length === 0) return null

    const dates = assets
      .filter(a => a.start_date && a.end_date)
      .flatMap(a => [a.start_date!, a.end_date!])

    if (dates.length === 0) return null

    const sorted = dates.sort()
    return {
      start_date: sorted[0],
      end_date: sorted[sorted.length - 1],
      days: Math.ceil((new Date(sorted[sorted.length - 1]).getTime() - new Date(sorted[0]).getTime()) / (1000 * 60 * 60 * 24)) + 1
    }
  }, [detail.data])

  const recheck = useMutation({
    mutationFn: () => api.revalidate(planId!, activeVersion ?? undefined),
    onSuccess: setRevalidation,
  })

  const submit = useMutation({
    mutationFn: () => api.submitPlan(planId!, activeVersion ?? undefined, comments || undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['plan', planId] })
      void approvals.refetch()
      setComments('')
    },
  })

  const decide = useMutation({
    mutationFn: (approve: boolean) =>
      api.decide(planId!, {
        version: activeVersion,
        approve,
        comments: comments || null,
        acknowledged_assumptions: acknowledged,
      }),
    onSuccess: (result) => {
      setRevalidation((result.revalidation as RevalidationResult) ?? null)
      void queryClient.invalidateQueries({ queryKey: ['plan', planId] })
      void approvals.refetch()
      setComments('')
    },
  })

  const downloadBlocking = useMutation({
    mutationFn: () =>
      api.downloadBlockingFile({
        plan_id: planId!,
        version: plan.data!.approved_version!,
        expected_campaign_name: blockingForm.campaignName.trim() || undefined,
        package_type: blockingForm.packageType,
        wrap_type: blockingForm.wrapType.trim() || undefined,
        foc_days: blockingForm.focDays,
        digital_slot: blockingForm.digitalSlot.trim() || undefined,
        agency_commission_pct: blockingForm.agencyCommissionPct,
      }),
    onSuccess: ({ blob, filename }) => {
      saveBlob(blob, filename)
      setBlockingDownload({ filename })
      void queryClient.invalidateQueries({ queryKey: ['blocking-status', planId] })
    },
  })

  const createBlocking = useMutation({
    mutationFn: () =>
      api.createBlocking({
        plan_id: planId!,
        version: plan.data!.approved_version!,
        expected_campaign_name: blockingForm.campaignName.trim() || undefined,
        package_type: blockingForm.packageType,
        foc_days: blockingForm.focDays,
        digital_slot: blockingForm.digitalSlot.trim() || undefined,
      }),
    onSuccess: (handoff) => {
      queryClient.setQueryData(['blocking-status', planId, plan.data?.approved_version], handoff)
    },
  })

  // Whether this plan's blocking file has been downloaded and, later, whether
  // the resulting booking was confirmed created in Captura by hand - there is
  // no API to detect either step automatically yet, so both states here are
  // recorded explicitly (a download creates the row; the button below marks
  // it created).
  const blockingStatus = useQuery({
    queryKey: ['blocking-status', planId, plan.data?.approved_version],
    queryFn: () => api.getBlockingStatus(planId!, plan.data!.approved_version!),
    enabled: Boolean(planId && plan.data?.approved_version),
    retry: false,
  })
  const blockingNotDownloaded =
    blockingStatus.error instanceof RequestFailed && blockingStatus.error.status === 404

  const markBookingCreated = useMutation({
    mutationFn: () =>
      api.markBookingCreated({
        plan_id: planId!,
        version: plan.data!.approved_version!,
        captura_campaign_id: bookingConfirmForm.capturaCampaignId.trim() || undefined,
        notes: bookingConfirmForm.notes.trim() || undefined,
      }),
    onSuccess: (handoff) => {
      queryClient.setQueryData(['blocking-status', planId, plan.data?.approved_version], handoff)
      setBookingConfirmForm({ capturaCampaignId: '', notes: '' })
    },
  })

  // Hooks moved above conditional returns

  if (!planId) {
    return (
      <main className="main">
        <div className="page">
          <div className="page-head">
            <div className="grow">
              <h1>Plans</h1>
              <div className="page-sub">Draft, submitted and approved plans in this tenant.</div>
            </div>
            <button className="primary" onClick={() => navigate('/plans/new')}>
              New plan
            </button>
          </div>
          {plans.isLoading ? (
            <Skeleton rows={4} />
          ) : !plans.data?.length ? (
            <EmptyState
              title="No plans yet"
              action={
                <button className="primary" onClick={() => navigate('/plans/new')}>
                  Start a brief
                </button>
              }
            />
          ) : (
            <div className="grid two">
              {plans.data.map((item) => (
                <button
                  key={item.plan_id}
                  className="card"
                  style={{ textAlign: 'left', padding: 14, cursor: 'pointer' }}
                  onClick={() => navigate(`/plans/${item.plan_id}`)}
                >
                  <div className="row between">
                    <strong>{item.name}</strong>
                    <Chip
                      tone={
                        item.status === 'approved' || item.status === 'handed_off'
                          ? 'ok'
                          : item.status === 'submitted'
                            ? 'warn'
                            : 'neutral'
                      }
                    >
                      {item.status.replace(/_/g, ' ')}
                    </Chip>
                  </div>
                  <div className="tiny faint" style={{ marginTop: 4 }}>
                    {item.versions.length} version(s)
                    {item.approved_version ? ` · approved v${item.approved_version}` : ''}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </main>
    )
  }

  if (plan.isLoading || detail.isLoading) {
    return (
      <main className="main">
        <div className="page">
          <Skeleton rows={6} />
        </div>
      </main>
    )
  }
  if (plan.isError) {
    return (
      <main className="main">
        <div className="page">
          <ErrorCallout error={plan.error} onRetry={() => void plan.refetch()} />
        </div>
      </main>
    )
  }

  const planData = plan.data!
  const versionData = detail.data
  const metrics = versionData?.metrics
  const assumptions = versionData?.assumptions ?? []
  const allAcknowledged = assumptions.every((a) => acknowledged.includes(a.key))
  const canApprove = me?.permissions.includes('plan:approve') ?? false
  const canSubmit = me?.permissions.includes('plan:submit') ?? false
  const canCreate = me?.permissions.includes('campaign:create') ?? false
  const isApproved = planData.status === 'approved' || planData.status === 'handed_off'

  return (
    <main className="main">
      <div className="page">
        <div style={{ marginBottom: 10 }}>
          <StepIndicator step={3} />
        </div>
        <div className="page-head">
          <div className="grow">
            <h1>{planData.name}</h1>
            <div className="page-sub">
              Version {activeVersion} of {planData.versions.length}
              {versionData?.label ? ` · ${versionData.label}` : ''}
              {versionData?.change_summary ? ` · ${versionData.change_summary}` : ''}
              {versionData?.tokens_used && versionData.tokens_used > 0 ? ` · ${versionData.tokens_used.toLocaleString()} tokens` : ''}
            </div>
          </div>
          <Chip
            tone={
              isApproved ? 'ok' : planData.status === 'submitted' ? 'warn' : 'neutral'
            }
          >
            {planData.status.replace(/_/g, ' ')}
          </Chip>
          <button
            onClick={() => navigate(`/recommendations/${planData.brief_id}`)}
            title="Run a new recommendation for this plan's brief, then save it as a new version"
          >
            New recommendation
          </button>
          {planData.versions.length > 1 ? (
            <>
              <select
                value={activeVersion ?? ''}
                onChange={(e) => setVersion(Number(e.target.value))}
                style={{ width: 160 }}
                aria-label="Plan version"
              >
                {planData.versions.map((v) => (
                  <option key={v.version} value={v.version}>
                    v{v.version} · {v.asset_count} assets
                    {planData.approved_version === v.version ? ' (approved)' : ''}
                  </option>
                ))}
              </select>
              <button onClick={() => navigate(`/plans/${planId}/compare`)}>Compare</button>
            </>
          ) : null}
        </div>

        {submit.isError ? <ErrorCallout error={submit.error} /> : null}
        {decide.isError ? <ErrorCallout error={decide.error} /> : null}
        {downloadBlocking.isError ? <ErrorCallout error={downloadBlocking.error} /> : null}
        {recheck.isError ? <ErrorCallout error={recheck.error} /> : null}

        {blockingDownload ? (
          <Callout tone="ok" title={`Downloaded ${blockingDownload.filename}`}>
            Upload this file at Captura's Blockings screen to create the hold - review every row
            first, since some columns (package, wrap type, per-slot pricing) are best-effort
            derived from the plan and may need adjusting.
          </Callout>
        ) : null}

        {campaignWindow ? (
          <div className="card">
            <div className="card-head">
              <h2 className="grow">Campaign Window</h2>
            </div>
            <div className="card-body">
              <div className="grid three">
                <Metric
                  label="Start Date"
                  value={new Date(campaignWindow.start_date).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                  })}
                />
                <Metric
                  label="End Date"
                  value={new Date(campaignWindow.end_date).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                  })}
                />
                <Metric
                  label="Duration"
                  value={`${campaignWindow.days} days`}
                  note={dateRange(campaignWindow.start_date, campaignWindow.end_date)}
                />
              </div>
            </div>
          </div>
        ) : null}

        {metrics ? (
          <div className="grid four">
            <Metric label="Assets" value={metrics.asset_count} note={`${metrics.locality_count} localities`} />
            <Metric
              label="Total cost"
              value={moneyShort(metrics.total_cost_minor)}
              note={metrics.budget_minor ? `of ${moneyShort(metrics.budget_minor)}` : undefined}
            />
            <Metric label="Budget used" value={percent(metrics.budget_utilisation, 1)} />
            <Metric
              label="Mean suitability"
              value={metrics.mean_suitability?.toFixed(1) ?? '—'}
              note={
                metrics.mean_confidence ? `confidence ${percent(metrics.mean_confidence)}` : undefined
              }
            />
          </div>
        ) : null}

        {metrics && (metrics.media_type_breakdown || metrics.asset_class_breakdown) ? (
          <BundleCompositionBreakdown
            metrics={metrics}
            selectedAssets={versionData?.selected_assets ?? []}
          />
        ) : null}

        {metrics ? (
          <div className="grid three">
            <FeatureMetric label="Plan TG fit" feature={metrics.tg_fit} />
            <FeatureMetric label="Campaign effective OTS" feature={metrics.campaign_eots} />
            <FeatureMetric label="Campaign target impressions" feature={metrics.target_impressions} />
            <FeatureMetric label="CPTI" feature={metrics.cpti_minor} />
          </div>
        ) : null}

        <div className="grid two">
          <div className="card">
            <div className="card-head">
              <h2 className="grow">Selected assets</h2>
              <span className="small muted">
                {versionData?.selected_assets.length ?? 0} sites
              </span>
            </div>
            {/* Filters */}
            {(availableMediaTypes.length > 0 || availableAssetClasses.length > 0) && (
              <div className="card-body tight" style={{ borderBottom: '1px solid var(--border)' }}>
                <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  {availableMediaTypes.length > 0 && (
                    <div className="row tight" style={{ gap: 4 }}>
                      <span className="tiny faint">Media Type:</span>
                      {availableMediaTypes.map((type) => (
                        <button
                          key={type}
                          className={`chip ${selectedMediaTypes.includes(type as string) ? 'primary' : 'neutral'}`}
                          onClick={() =>
                            setSelectedMediaTypes((prev: any) =>
                              prev.includes(type)
                                ? prev.filter((t: string) => t !== type)
                                : [...prev, type]
                            )
                          }
                          style={{ cursor: 'pointer' }}
                        >
                          {type}
                        </button>
                      ))}
                      {selectedMediaTypes.length > 0 && (
                        <button
                          className="tiny ghost"
                          onClick={() => setSelectedMediaTypes([])}
                          style={{ padding: '2px 6px' }}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  )}
                  {availableAssetClasses.length > 0 && (
                    <div className="row tight" style={{ gap: 4 }}>
                      <span className="tiny faint">Asset Class:</span>
                      {availableAssetClasses.map((assetClass) => (
                        <button
                          key={assetClass}
                          className={`chip ${selectedAssetClasses.includes(assetClass as string) ? 'primary' : 'neutral'}`}
                          onClick={() =>
                            setSelectedAssetClasses((prev: any) =>
                              prev.includes(assetClass)
                                ? prev.filter((c: string) => c !== assetClass)
                                : [...prev, assetClass]
                            )
                          }
                          style={{ cursor: 'pointer' }}
                        >
                          {assetClass}
                        </button>
                      ))}
                      {selectedAssetClasses.length > 0 && (
                        <button
                          className="tiny ghost"
                          onClick={() => setSelectedAssetClasses([])}
                          style={{ padding: '2px 6px' }}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {(selectedMediaTypes.length > 0 || selectedAssetClasses.length > 0) && (
                  <div className="tiny faint" style={{ marginTop: 6 }}>
                    Showing {filteredAssets.length} of {versionData?.selected_assets.length ?? 0} assets
                  </div>
                )}
              </div>
            )}
            <div className="table-wrap" style={{ maxHeight: 420 }}>
              <table>
                <thead>
                  <tr>
                    <th>Asset</th>
                    <th className="num">Score</th>
                    <th className="num">Cost</th>
                    <th>Window</th>
                  </tr>
                </thead>
                <tbody>
                  {versionData?.selected_assets.map((asset) => (
                    <tr key={asset.captura_asset_id}>
                      <td>
                        <div style={{ fontWeight: 550 }}>
                          {asset.name}
                          {asset.locked ? <Chip tone="neutral"> locked</Chip> : null}
                          {asset.manually_added ? <Chip tone="warn"> added by hand</Chip> : null}
                        </div>
                        <div className="tiny faint">
                          {asset.media_type}
                          {asset.locality ? ` · ${asset.locality}` : ''}
                          {asset.city_name ? `, ${asset.city_name}` : ''}
                        </div>
                      </td>
                      <td className="num tnum">{asset.total_score?.toFixed(1) ?? '—'}</td>
                      <td className="num tnum nowrap">{moneyShort(asset.cost_minor)}</td>
                      <td className="tiny nowrap">
                        {dateRange(asset.start_date, asset.end_date)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2} style={{ fontWeight: 650 }}>
                      Total
                    </td>
                    <td className="num tnum" style={{ fontWeight: 650 }}>
                      {money(metrics?.total_cost_minor ?? 0)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div className="card" style={{ minHeight: 340, overflow: 'hidden' }}>
            <AssetMap assets={mapAssets} />
          </div>
        </div>

        {/* -- final recheck ------------------------------------------------ */}
        <div className="card">
          <div className="card-head">
            <h2 className="grow">Availability and price recheck</h2>
            <button
              className="small"
              onClick={() => recheck.mutate()}
              disabled={recheck.isPending}
            >
              {recheck.isPending ? 'Checking…' : 'Re-check now'}
            </button>
          </div>
          <div className="card-body stack tight">
            {!revalidation ? (
              <div className="small muted">
                Inventory can change between planning and booking, so it is re-read from Captura
                at approval and again immediately before creation.
              </div>
            ) : (
              <>
                <div className="row">
                  <Chip tone={revalidation.ok ? 'ok' : 'danger'}>
                    {revalidation.ok ? 'Clear' : 'Needs attention'}
                  </Chip>
                  <span className="small muted">
                    Checked {shortDate(revalidation.checked_at)} from{' '}
                    {revalidation.source.replace(/_/g, ' ')}
                    {revalidation.new_total_cost_minor !== null
                      ? ` · re-priced at ${moneyShort(revalidation.new_total_cost_minor)}`
                      : ''}
                  </span>
                </div>
                {revalidation.blocking_issues.map((issue, i) => (
                  <Callout key={i} tone="danger" title={issue} />
                ))}
                {revalidation.warnings.map((warning, i) => (
                  <Callout key={i} tone="warn" title={warning} />
                ))}
                {revalidation.unavailable.length ? (
                  <div className="small">
                    <strong>No longer available:</strong>
                    <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                      {revalidation.unavailable.map((item, i) => (
                        <li key={i}>
                          {String(item.name)} — {String(item.reason)}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {revalidation.price_changed.length ? (
                  <div className="small">
                    <strong>Price changed:</strong>
                    <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                      {revalidation.price_changed.map((item, i) => (
                        <li key={i}>
                          {String(item.name)}: {moneyShort(Number(item.old_cost_minor))} →{' '}
                          {item.new_cost_minor
                            ? moneyShort(Number(item.new_cost_minor))
                            : 'no price'}
                          {item.change_pct ? ` (${Number(item.change_pct).toFixed(1)}%)` : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>

        {/* -- assumptions and approval ------------------------------------- */}
        <div className="card">
          <div className="card-head">
            <h2 className="grow">Assumptions</h2>
            <span className="small muted">
              {acknowledged.length} of {assumptions.length} acknowledged
            </span>
          </div>
          <div className="card-body stack tight">
            <div className="small muted">
              Approval requires acknowledging each of these. They record what the numbers above do
              and do not mean.
            </div>
            {assumptions.map((assumption) => (
              <label
                key={assumption.key}
                className={`callout ${assumption.severity === 'critical'
                  ? 'danger'
                  : assumption.severity === 'caution'
                    ? 'warn'
                    : 'info'
                  }`}
                style={{ cursor: canApprove ? 'pointer' : 'default' }}
              >
                <input
                  type="checkbox"
                  style={{ width: 'auto', marginTop: 2 }}
                  disabled={!canApprove || isApproved}
                  checked={acknowledged.includes(assumption.key)}
                  onChange={(e) =>
                    setAcknowledged((prev) =>
                      e.target.checked
                        ? [...prev, assumption.key]
                        : prev.filter((k) => k !== assumption.key),
                    )
                  }
                />
                <div className="body">
                  <strong>{assumption.statement}</strong>
                  {assumption.impact ? <div className="small">{assumption.impact}</div> : null}
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2 className="grow">Decision</h2>
          </div>
          <div className="card-body stack">
            <label className="field">
              Comment
              <textarea
                rows={2}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Recorded on the approval, visible in the audit log…"
              />
            </label>
            <div className="row">
              {planData.status === 'draft' || planData.status === 'rejected' ? (
                <button
                  className="primary"
                  disabled={!canSubmit || submit.isPending}
                  onClick={() => submit.mutate()}
                  title={canSubmit ? undefined : 'Your role cannot submit a plan'}
                >
                  {submit.isPending ? 'Submitting…' : 'Submit for approval'}
                </button>
              ) : null}

              {planData.status === 'submitted' ? (
                <>
                  <button
                    className="primary"
                    disabled={!canApprove || !allAcknowledged || decide.isPending}
                    onClick={() => decide.mutate(true)}
                    title={
                      !canApprove
                        ? 'Your role cannot approve'
                        : !allAcknowledged
                          ? 'Acknowledge every assumption first'
                          : undefined
                    }
                  >
                    {decide.isPending ? 'Deciding…' : 'Approve'}
                  </button>
                  <button
                    className="danger"
                    disabled={!canApprove || decide.isPending}
                    onClick={() => decide.mutate(false)}
                  >
                    Reject
                  </button>
                </>
              ) : null}

              {!canApprove && planData.status === 'submitted' ? (
                <span className="small muted">
                  Waiting on an approver. A planner cannot approve their own plan.
                </span>
              ) : null}
            </div>
          </div>
        </div>

        {isApproved ? (
          <div className="card">
            <div className="card-head">
              <h2 className="grow">Hand off to Captura</h2>
              <span className="tiny faint">
                {versionData?.selected_assets.length ?? 0} asset(s)
              </span>
            </div>
            <div className="card-body stack">
              <Callout tone="info" title="A Blocking is a hold, not a booking">
                Create the Blocking directly in Captura, or download its upload template if no
                Captura API is configured here - either way this never books a real campaign,
                only Captura's lightweight, cancellable hold on the selected assets.
              </Callout>
              <div className="grid two">
                <label className="field">
                  <span className="row tight nowrap" style={{ gap: 4 }}>
                    Campaign name
                    <InfoTip text="Shown against every row. Defaults to this plan's name if left blank." />
                  </span>
                  <input
                    placeholder={planData.name}
                    value={blockingForm.campaignName}
                    onChange={(e) =>
                      setBlockingForm((f) => ({ ...f, campaignName: e.target.value }))
                    }
                  />
                </label>
                <label className="field">
                  <span className="row tight nowrap" style={{ gap: 4 }}>
                    Package
                    <InfoTip text="Captura's package tier for this booking. Applied to every row." />
                  </span>
                  <select
                    value={blockingForm.packageType}
                    onChange={(e) =>
                      setBlockingForm((f) => ({ ...f, packageType: e.target.value }))
                    }
                  >
                    {PACKAGE_OPTIONS.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span className="row tight nowrap" style={{ gap: 4 }}>
                    Wrap type
                    <InfoTip text="Download-only: Captura's Blocking record has no wrap-type column, so this only appears in the downloaded file. Left blank ('-') unless this booking needs a specific wrap." />
                  </span>
                  <input
                    placeholder="-"
                    value={blockingForm.wrapType}
                    onChange={(e) => setBlockingForm((f) => ({ ...f, wrapType: e.target.value }))}
                  />
                </label>
                <label className="field">
                  <span className="row tight nowrap" style={{ gap: 4 }}>
                    Digital slot
                    <InfoTip text="Only applied to digital assets in this plan; static rows always get '-'." />
                  </span>
                  <input
                    placeholder="-"
                    value={blockingForm.digitalSlot}
                    onChange={(e) =>
                      setBlockingForm((f) => ({ ...f, digitalSlot: e.target.value }))
                    }
                  />
                </label>
                <label className="field">
                  <span className="row tight nowrap" style={{ gap: 4 }}>
                    FOC days
                    <InfoTip text="Free-of-charge days added to every asset's booking, e.g. for makegoods." />
                  </span>
                  <input
                    type="number"
                    min={0}
                    value={blockingForm.focDays}
                    onChange={(e) =>
                      setBlockingForm((f) => ({ ...f, focDays: Number(e.target.value) || 0 }))
                    }
                  />
                </label>
                <label className="field">
                  <span className="row tight nowrap" style={{ gap: 4 }}>
                    Agency commission %
                    <InfoTip text="Download-only: Captura's Blocking record has no agency-commission column, so this only appears in the downloaded file's AGENCY COMM column." />
                  </span>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={blockingForm.agencyCommissionPct}
                    onChange={(e) =>
                      setBlockingForm((f) => ({
                        ...f,
                        agencyCommissionPct: Number(e.target.value) || 0,
                      }))
                    }
                  />
                </label>
              </div>
              <div className="row">
                <button
                  className="primary"
                  disabled={!canCreate || createBlocking.isPending}
                  onClick={() => createBlocking.mutate()}
                  title={
                    canCreate
                      ? 'Creates the Blocking directly in Captura via its API'
                      : 'Your role cannot hand off this plan'
                  }
                >
                  {createBlocking.isPending ? 'Creating…' : 'Create blocking in Captura'}
                </button>
                <button
                  disabled={!canCreate || downloadBlocking.isPending}
                  onClick={() => downloadBlocking.mutate()}
                  title={
                    canCreate
                      ? 'Downloads the Captura Blocking upload file for this plan, for a Captura user to upload by hand'
                      : 'Your role cannot hand off this plan'
                  }
                >
                  {downloadBlocking.isPending ? 'Preparing…' : 'Download blocking file instead'}
                </button>
              </div>

              {createBlocking.isError ? <ErrorCallout error={createBlocking.error} /> : null}
              {markBookingCreated.isError ? <ErrorCallout error={markBookingCreated.error} /> : null}

              {blockingStatus.data?.status === 'created' ? (
                <Callout tone="ok" title="Blocking created in Captura">
                  Created {shortDate(blockingStatus.data.downloaded_at)}
                  {blockingStatus.data.captura_blocking_id
                    ? ` · Captura blocking ${blockingStatus.data.captura_blocking_id}`
                    : ''}
                </Callout>
              ) : blockingStatus.data?.status === 'rejected' || blockingStatus.data?.status === 'failed' ? (
                <Callout
                  tone="danger"
                  title={
                    blockingStatus.data.status === 'rejected'
                      ? 'Captura rejected the last attempt'
                      : 'The last attempt could not reach Captura'
                  }
                >
                  {blockingStatus.data.notes ?? 'Retry, or use the download option instead.'}
                </Callout>
              ) : blockingStatus.data?.status === 'booking_created' ? (
                <Callout tone="ok" title="Booking created in Captura">
                  Confirmed {shortDate(blockingStatus.data.booking_created_at!)}
                  {blockingStatus.data.captura_campaign_id
                    ? ` · Captura campaign ${blockingStatus.data.captura_campaign_id}`
                    : ''}
                  {blockingStatus.data.notes ? (
                    <div className="tiny muted" style={{ marginTop: 4 }}>
                      {blockingStatus.data.notes}
                    </div>
                  ) : null}
                </Callout>
              ) : blockingStatus.data?.status === 'downloaded' ? (
                <Callout tone="warn" title="Blocking file downloaded, booking not yet confirmed">
                  <div className="tiny muted" style={{ marginBottom: 8 }}>
                    Downloaded {shortDate(blockingStatus.data.downloaded_at)}. Once the file is
                    uploaded and the booking created in Captura, confirm it here - there is no API
                    to detect that automatically yet.
                  </div>
                  <div className="row tight" style={{ marginBottom: 8 }}>
                    <input
                      placeholder="Captura campaign ID (optional)"
                      value={bookingConfirmForm.capturaCampaignId}
                      onChange={(e) =>
                        setBookingConfirmForm((f) => ({ ...f, capturaCampaignId: e.target.value }))
                      }
                      style={{ maxWidth: 220 }}
                    />
                    <input
                      placeholder="Notes (optional)"
                      value={bookingConfirmForm.notes}
                      onChange={(e) => setBookingConfirmForm((f) => ({ ...f, notes: e.target.value }))}
                      className="grow"
                    />
                  </div>
                  <button
                    className="primary small"
                    disabled={markBookingCreated.isPending}
                    onClick={() => markBookingCreated.mutate()}
                  >
                    {markBookingCreated.isPending ? 'Saving…' : 'Mark booking created'}
                  </button>
                </Callout>
              ) : blockingNotDownloaded ? null : blockingStatus.isError ? (
                <ErrorCallout error={blockingStatus.error} />
              ) : null}
            </div>
          </div>
        ) : null}

        {approvals.data?.length ? (
          <div className="card">
            <div className="card-head">
              <h2 className="grow">Approval history</h2>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Version</th>
                    <th>Decision</th>
                    <th>Actor</th>
                    <th>Comment</th>
                  </tr>
                </thead>
                <tbody>
                  {approvals.data.map((row: any, i: number) => (
                    <tr key={i}>
                      <td className="tiny nowrap">{shortDate(row.created_at)}</td>
                      <td className="num">v{row.version}</td>
                      <td>
                        <Chip
                          tone={
                            row.decision === 'approved'
                              ? 'ok'
                              : row.decision === 'rejected'
                                ? 'danger'
                                : 'neutral'
                          }
                        >
                          {row.decision}
                        </Chip>
                      </td>
                      <td className="small">{row.actor_email}</td>
                      <td className="small muted">{row.comments ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    </main>
  )
}

function BundleCompositionBreakdown({
  metrics,
  selectedAssets,
}: {
  metrics: PlanVersionOut['metrics']
  selectedAssets: SelectedAsset[]
}) {
  if (!metrics) return null

  // Calculate cost breakdown by media type
  const costByMediaType: Record<string, number> = {}
  const costByAssetClass: Record<string, number> = {}

  selectedAssets.forEach((asset) => {
    const cost = asset.cost_minor || 0
    const mediaType = asset.media_type || 'Unknown'
    const assetClass = asset.asset_class || 'Unknown'
    const displayClass = assetClass === 'static' ? 'Static' : assetClass === 'transit' ? 'Transit' : assetClass === 'dooh' ? 'DOOH' : assetClass

    costByMediaType[mediaType] = (costByMediaType[mediaType] || 0) + cost
    costByAssetClass[displayClass] = (costByAssetClass[displayClass] || 0) + cost
  })

  const totalCost = metrics.total_cost_minor || 1
  const totalAssets = metrics.asset_count || 1

  const mediaTypeEntries = Object.entries(metrics.media_type_breakdown || {})
    .sort((a, b) => b[1] - a[1])
  const assetClassEntries = Object.entries(metrics.asset_class_breakdown || {})
    .sort((a, b) => b[1] - a[1])

  return (
    <div className="card">
      <div className="card-head">
        <h2 className="grow">Bundle Composition</h2>
      </div>
      <div className="card-body">
        {mediaTypeEntries.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div className="small" style={{ fontWeight: 600, marginBottom: 6 }}>By Media Type</div>
            <div className="stack tight">
              {mediaTypeEntries.map(([mediaType, count]) => {
                const cost = costByMediaType[mediaType] || 0
                const assetPct = (count / totalAssets) * 100
                const costPct = totalCost > 0 ? (cost / totalCost) * 100 : 0

                return (
                  <div key={mediaType} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                      <div className="small" style={{ fontWeight: 500, minWidth: 0, flex: '1 1 auto' }}>{mediaType}</div>
                      <div className="tiny faint" style={{ whiteSpace: 'nowrap', flex: '0 0 auto' }}>
                        {count} assets ({assetPct.toFixed(1)}%)
                      </div>
                    </div>
                    <div className="tiny faint" style={{ marginBottom: 2 }}>
                      {moneyShort(cost)} ({costPct.toFixed(1)}% of cost)
                    </div>
                    <div className="bar" style={{ height: 6, background: 'var(--gray-200)', width: '100%' }}>
                      <span
                        style={{
                          width: `${Math.min(100, costPct)}%`,
                          background: 'var(--accent)'
                        }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {assetClassEntries.length > 0 && (
          <div>
            <div className="small" style={{ fontWeight: 600, marginBottom: 6 }}>By Asset Class</div>
            <div className="stack tight">
              {assetClassEntries.map(([assetClass, count]) => {
                const displayClass = ASSET_CLASS_LABEL[assetClass as AssetClass] || assetClass
                const cost = costByAssetClass[displayClass] || 0
                const assetPct = (count / totalAssets) * 100
                const costPct = totalCost > 0 ? (cost / totalCost) * 100 : 0

                return (
                  <div key={assetClass} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                      <div className="small" style={{ fontWeight: 500, minWidth: 0, flex: '1 1 auto' }}>{displayClass}</div>
                      <div className="tiny faint" style={{ whiteSpace: 'nowrap', flex: '0 0 auto' }}>
                        {count} assets ({assetPct.toFixed(1)}%)
                      </div>
                    </div>
                    <div className="tiny faint" style={{ marginBottom: 2 }}>
                      {moneyShort(cost)} ({costPct.toFixed(1)}% of cost)
                    </div>
                    <div className="bar" style={{ height: 6, background: 'var(--gray-200)', width: '100%' }}>
                      <span
                        style={{
                          width: `${Math.min(100, costPct)}%`,
                          background: 'var(--accent-secondary, var(--accent))'
                        }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
